<!-- Phase 3 create-modal template: BUI-styled form content hosted inside the
     shared Dialog.vue wrapper (see CreateSlideButtonWithDialog.vue). -->

<template>
  <div class="create-slide-form">
    <StatusBar
      :errorMessage="errorMessage"
      :isLoading="isLoading"
      loadingMessage="Saving..."
      :isSuccess="isSuccess"
      successMessage="Saved!"
    />
    <div class="field-content">
      <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
      <div class="d-flex field-row">
        <label for="csl-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
        <TextField id="csl-name" name="name" data-test-id="slide-name" v-model="values.name" class="basis-1/2" width="100%" />
      </div>
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Dimensions &#9733;</label>
        <SelectField
          class="basis-1/2 csl-select"
          :options="dimensionsOptionsConfig"
          v-model="values.dimensions"
        />
      </div>
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Orientation &#9733;</label>
        <SelectField
          class="basis-1/2 csl-select"
          :options="orientationOptionsConfig"
          v-model="values.orientation"
        />
      </div>
      <div class="d-flex field-row">
        <label for="csl-width" class="basis-1/2 text-sm font-bold">Width &#9733;</label>
        <NumberField
          id="csl-width"
          name="width"
          data-test-id="slide-width"
          v-model="values.width"
          :disabled="values.dimensions != ''"
          class="basis-1/2"
          width="100%"
        />
      </div>
      <div class="d-flex field-row">
        <label for="csl-height" class="basis-1/2 text-sm font-bold">Height &#9733;</label>
        <NumberField
          id="csl-height"
          name="height"
          data-test-id="slide-height"
          v-model="values.height"
          :disabled="values.dimensions != ''"
          class="basis-1/2"
          width="100%"
        />
      </div>
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Background Color &#9733;</label>
        <div class="basis-1/2 csl-color">
          <ColorField v-model="values.backgroundColor" />
        </div>
      </div>
    </div>
    <div class="form-buttons">
      <ButtonCustomized label="Cancel" data-testid="slide-cancel" @click="onCancel" ctaSecondary />
      <ButtonCustomized label="Create" data-testid="slide-create" cta @click="onSubmit" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import SelectField from "@/components/form-fields/SelectField/SelectField.vue";
import NumberField from "@/components/form-fields/NumberField.vue";
import TextField from "@/components/form-fields/TextField.vue";
import ColorField from "@/components/form-fields/ColorField/ColorField.vue";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";

import useCreateSlideFormHelpers from "./composables/createSlideFormHelpers";
import useNotifications from "@/components/Notifications/composables/notifications";

import router from '@/router';

import {
  dimensionsOptionsConfig,
  orientationOptionsConfig
} from "./config/options";

const emit = defineEmits(['submit', 'cancel', 'success', 'fail'])

const notification = useNotifications();

const { values, submitForm, isLoading, isSuccess, errorMessage, newSlideId } = useCreateSlideFormHelpers();

async function onSubmit() {
  await submitForm();
  if(isSuccess.value === true) {
    notification.success('Slide created successfully!');
    emit('success');
    router.push({name: 'media-editor', params: {id: newSlideId.value}});
  } else {    // on failure
    emit('fail');
  }
}

function onCancel() {
  emit('cancel');
}
</script>

<style lang="scss" scoped>
.create-slide-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: min(26rem, calc(100vw - 6rem));
  color: var(--bui-tbl-row-text, #2a2969);
}

.field-content {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  :deep(.field label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(.field input[type='text']),
  :deep(.field input[type='number']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    width: 100%;
  }
}

.field-row {
  align-items: center;

  label {
    color: var(--bui-tbl-row-text, #2a2a30);
  }
}

/* Legacy SelectField shell → tokens. */
.csl-select {
  :deep(.styled-select) {
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    border-radius: var(--bui-ter-radius, 8px);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

    select {
      color: var(--bui-ter-input-text, #1a1a1e);
    }
  }
}

/* ColorField: keep the swatch and hex input side by side. */
.csl-color {
  :deep(.field) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :deep(input[type='text']) {
    width: 11ch !important;
  }
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}
</style>
